import type { TFunction } from 'i18next';
import type { Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { getEnumNumericKeys } from 'libs/utils';
import type {
  CompanyAlertType,
  NewAlertTypeCustomProperty,
  NewSelectableOption,
} from 'model/Alerts/types';
import {
  CompanyAlertBaseType,
  CustomPropertyRequired,
  CustomPropertyType,
} from 'model/Alerts/types';

export type {
  AlertTypeCustomProperty,
  NewAlertTypeCustomProperty,
} from 'model/Alerts/types';
export { CustomPropertyRequired } from 'model/Alerts/types';

export interface AlertTypeFormShape {
  baseType: CompanyAlertBaseType;
  name: string;
  serialPrefix: string;
  hmsRegTypeId: string | null;
  externalNumber: string | null;
  responsible: string[];
  active: boolean;
  availableOnApp: boolean;
  sendEmail: boolean;
  publishInFeed: boolean;
  linkOnlyAvailable: boolean;
  damageInfo: boolean;
  imageRequired: boolean;
  exportable: boolean;
  risk: boolean;
  cost: boolean;
  costRequired: boolean;
  categories: NewSelectableOption[];
  workRelatedTo: NewSelectableOption[];
  cause: NewSelectableOption[];
  involvedParties: NewSelectableOption[];
  customProperties: NewAlertTypeCustomProperty[];
}

export const mapAlertTypeToForm = (
  alertType: CompanyAlertType
): AlertTypeFormShape => ({
  baseType: alertType.baseType,
  name: alertType.name,
  serialPrefix: alertType.serialPrefix ?? '',
  hmsRegTypeId: alertType.hmsRegTypeId,
  externalNumber: alertType.externalNumber,
  responsible: alertType.responsible,
  active: alertType.active,
  availableOnApp: alertType.availableOnApp,
  sendEmail: alertType.sendEmail,
  publishInFeed: alertType.publishInFeed,
  linkOnlyAvailable: alertType.linkOnlyAvailable,
  damageInfo: alertType.damageInfo,
  imageRequired: alertType.imageRequired,
  exportable: alertType.exportable,
  risk: alertType.risk,
  cost: alertType.cost,
  costRequired: alertType.costRequired,
  categories: alertType.categories,
  workRelatedTo: alertType.workRelatedTo,
  cause: alertType.cause,
  involvedParties: alertType.involvedParties,
  customProperties: alertType.customProperties,
});

const makeOptionSchema = (t: TFunction) =>
  Yup.object().shape({
    id: Yup.string().optional(),
    name: Yup.string()
      .isNotWhiteSpace(t('ValidationError.Required'))
      .required(t('ValidationError.Required')),
  });

export const useAlertTypeValidationSchema = () => {
  const { t } = useTranslation();

  return Yup.object().shape({
    baseType: Yup.number()
      .oneOf(getEnumNumericKeys(CompanyAlertBaseType))
      .default(CompanyAlertBaseType.HSE),
    name: Yup.string()
      .isNotWhiteSpace(t('ValidationError.Required'))
      .required(t('ValidationError.Required')),
    serialPrefix: Yup.string()
      .isNotWhiteSpace(t('ValidationError.Required'))
      .required(t('ValidationError.Required')),
    hmsRegTypeId: Yup.string().nullable().default(null),
    externalNumber: Yup.string().nullable().default(null),
    responsible: Yup.array().of(Yup.string().defined()).default([]),
    active: Yup.boolean().default(true),
    availableOnApp: Yup.boolean().default(true),
    sendEmail: Yup.boolean().default(false),
    publishInFeed: Yup.boolean().default(false),
    linkOnlyAvailable: Yup.boolean().default(false),
    risk: Yup.boolean().default(false),
    damageInfo: Yup.boolean().default(false),
    imageRequired: Yup.boolean().default(true),
    exportable: Yup.boolean().default(false),
    cost: Yup.boolean().default(true),
    costRequired: Yup.boolean().default(false),
    categories: Yup.array().of(makeOptionSchema(t)).default([]),
    workRelatedTo: Yup.array().of(makeOptionSchema(t)).default([]),
    cause: Yup.array().of(makeOptionSchema(t)).default([]),
    involvedParties: Yup.array().of(makeOptionSchema(t)).default([]),
    customProperties: Yup.lazy(
      (_, { parent }: { parent: AlertTypeFormShape }) =>
        Yup.array()
          .of(
            Yup.object().shape({
              id: Yup.string().nullable().default(null),
              type: Yup.number()
                .oneOf(getEnumNumericKeys(CustomPropertyType))
                .default(CustomPropertyType.Dropdown),
              name: Yup.string()
                .isNotWhiteSpace(t('ValidationError.Required'))
                .required(t('ValidationError.Required')),
              datasetId: Yup.string()
                .when('type', {
                  is: (type: CustomPropertyType) =>
                    type === CustomPropertyType.Dataset,
                  then: (schema) =>
                    schema
                      .isNotWhiteSpace(t('ValidationError.Required'))
                      .required(t('ValidationError.Required')),
                })
                .nullable()
                .defined(),
              options: Yup.array()
                .of(makeOptionSchema(t))
                .when('type', {
                  is: (type: CustomPropertyType) =>
                    type === CustomPropertyType.Dropdown,
                  then: (schema) =>
                    schema.min(
                      1,
                      t('Alerts.CompanyTypeSettings.ProvideAtLeastOneOption')
                    ),
                })
                .default([]),
              availableOnApp: Yup.boolean()
                .test(
                  'only-one-available-on-app',
                  t('Alerts.CompanyTypeSettings.OnlyOneAvailableOnApp'),
                  () =>
                    parent.customProperties.filter(
                      (property) => property.availableOnApp
                    ).length <= 1
                )
                .default(false),
              required: Yup.number()
                .oneOf(getEnumNumericKeys(CustomPropertyRequired))
                .default(CustomPropertyRequired.Optional),
            })
          )
          .default([])
    ),
  });
};

export const alertTypeDefaultValues: AlertTypeFormShape = {
  baseType: CompanyAlertBaseType.HSE,
  name: '',
  serialPrefix: '',
  hmsRegTypeId: null,
  externalNumber: null,
  responsible: [],
  active: true,
  availableOnApp: true,
  sendEmail: false,
  publishInFeed: false,
  linkOnlyAvailable: false,
  risk: false,
  damageInfo: false,
  imageRequired: true,
  exportable: false,
  cost: true,
  costRequired: false,
  categories: [],
  workRelatedTo: [],
  cause: [],
  involvedParties: [],
  customProperties: [],
};

export const alertDefaultCustomProperty: NewAlertTypeCustomProperty = {
  id: null,
  name: '',
  type: CustomPropertyType.Dropdown,
  options: [
    {
      name: '',
    },
    {
      name: '',
    },
    {
      name: '',
    },
  ],
  datasetId: null,
  availableOnApp: false,
  required: CustomPropertyRequired.Optional,
};

export type AlertTypePathWithOptions = Path<AlertTypeFormShape> &
  {
    [K in keyof AlertTypeFormShape]: AlertTypeFormShape[K] extends NewSelectableOption[]
      ? K
      : never;
  }[keyof AlertTypeFormShape];

export type AlertTypeCustomPropertyPath = `${Path<AlertTypeFormShape> &
  {
    [K in keyof AlertTypeFormShape]: AlertTypeFormShape[K] extends NewAlertTypeCustomProperty[]
      ? K
      : never;
  }[keyof AlertTypeFormShape]}.${number}`;
